import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, CalendarDays, Plus, CreditCard, ArrowUp, ArrowDown, Scale,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { formatCurrency } from '../utils/format';
import LancamentoModal from '../components/LancamentoModal';

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const pad = (n) => String(n).padStart(2, '0');
const iso = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const todayIso = () => { const d = new Date(); return iso(d.getFullYear(), d.getMonth(), d.getDate()); };
const short = (v) => `R$ ${Math.round(v).toLocaleString('pt-BR')}`;

function buildItems(lancamentos, cartoesFaturas, hoje) {
  const items = [];
  lancamentos.forEach((l) => {
    if (l.tipo === 'TRANSFERENCIA' || l.ajusteSaldo || l.cartaoId) return;
    const date = (l.dataPagamento || l.dataCompetencia || '').slice(0, 10);
    if (!date) return;
    const pending = l.status === 'pendente' || l.status === 'atrasado';
    items.push({
      key: `l-${l.id}`, source: 'lancamento', raw: l, date,
      kind: l.tipo === 'RECEITA' ? 'entrada' : 'saida',
      title: l.descricao, valor: Number(l.valor) || 0, categoria: l.categoria,
      pending, overdue: l.status === 'atrasado' || (pending && date < hoje),
    });
  });
  cartoesFaturas.forEach(({ cartao, faturas }) => {
    faturas.forEach((f) => {
      const valor = Number(f.totalPendente || 0);
      if (f.status === 'paga' || valor <= 0 || !f.dataVencimento) return;
      items.push({
        key: `f-${cartao.id}-${f.faturaReferencia}`, source: 'fatura', cartao,
        date: f.dataVencimento, kind: 'saida', title: `Fatura ${cartao.nome}`,
        valor, categoria: 'Cartão de crédito', pending: true, overdue: f.dataVencimento < hoje,
      });
    });
  });
  return items;
}

export default function Calendario() {
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const hoje = todayIso();
  const now = new Date();

  const [cursor, setCursor] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [selected, setSelected] = useState(hoje);
  const [lancamentos, setLancamentos] = useState([]);
  const [cartoesFaturas, setCartoesFaturas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, lancamento: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lRes, cRes] = await Promise.all([api.get('/lancamentos'), api.get('/cartoes')]);
      setLancamentos(lRes.data || []);
      const cartoes = cRes.data || [];
      const faturas = await Promise.all(cartoes.map(async (cartao) => {
        try {
          const r = await api.get(`/cartoes/${cartao.id}/faturas`);
          return { cartao, faturas: r.data || [] };
        } catch { return { cartao, faturas: [] }; }
      }));
      setCartoesFaturas(faturas);
    } catch {
      toast.error('Erro ao carregar o calendário.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const items = useMemo(() => buildItems(lancamentos, cartoesFaturas, hoje), [lancamentos, cartoesFaturas, hoje]);

  const byDay = useMemo(() => {
    const map = {};
    items.forEach((it) => { (map[it.date] = map[it.date] || []).push(it); });
    return map;
  }, [items]);

  const monthPrefix = `${cursor.y}-${pad(cursor.m + 1)}`;
  const totals = useMemo(() => {
    const t = { recebido: 0, pago: 0, aReceber: 0, aPagar: 0 };
    items.forEach((it) => {
      if (!it.date.startsWith(monthPrefix)) return;
      if (it.kind === 'entrada') { if (it.pending) t.aReceber += it.valor; else t.recebido += it.valor; }
      else if (it.pending) t.aPagar += it.valor; else t.pago += it.valor;
    });
    return t;
  }, [items, monthPrefix]);

  const first = new Date(cursor.y, cursor.m, 1);
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const cells = [...Array(first.getDay()).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  const goMonth = (delta) => {
    const d = new Date(cursor.y, cursor.m + delta, 1);
    setCursor({ y: d.getFullYear(), m: d.getMonth() });
    setSelected(iso(d.getFullYear(), d.getMonth(), 1));
  };
  const goToday = () => {
    const d = new Date();
    setCursor({ y: d.getFullYear(), m: d.getMonth() });
    setSelected(todayIso());
  };

  const selItems = byDay[selected] || [];
  const selDate = new Date(`${selected}T00:00:00`);
  const rawLabel = selDate.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' });
  const selLabel = rawLabel.charAt(0).toUpperCase() + rawLabel.slice(1);

  const openItem = (it) => {
    if (it.source === 'fatura') navigate(`/${tenantSlug}/cartoes`);
    else setModal({ open: true, lancamento: it.raw });
  };

  return (
    <div className="w-full max-w-6xl mx-auto space-y-5 page-enter pb-16" data-testid="calendario-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2.5" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            <span className="w-9 h-9 rounded-xl bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shadow-sm">
              <CalendarDays size={20} />
            </span>
            Calendário
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            Vencimentos, recebimentos e faturas de cartão dia a dia
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={goToday} className="fin-btn-secondary text-xs !py-2 !px-3" data-testid="cal-hoje">Hoje</button>
          <div className="flex items-center gap-1 fin-card !rounded-2xl p-1">
            <button onClick={() => goMonth(-1)} aria-label="Mês anterior" data-testid="cal-prev" className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-white/5 text-slate-500"><ChevronLeft size={18} /></button>
            <span className="min-w-[132px] text-center text-sm font-bold text-slate-800 dark:text-slate-100" data-testid="cal-mes">{MESES[cursor.m]} {cursor.y}</span>
            <button onClick={() => goMonth(1)} aria-label="Próximo mês" data-testid="cal-next" className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-white/5 text-slate-500"><ChevronRight size={18} /></button>
          </div>
        </div>
      </div>

      {/* Resumo do mês */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Recebido', value: totals.recebido, icon: ArrowUp, color: '#10B981' },
          { label: 'Pago', value: totals.pago, icon: ArrowDown, color: '#EF4444' },
          { label: 'A receber', value: totals.aReceber, icon: Scale, color: '#6366F1' },
          { label: 'A pagar', value: totals.aPagar, icon: CreditCard, color: '#F59E0B' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="fin-card p-3.5 flex items-center gap-3">
            <span className="w-9 h-9 rounded-full flex items-center justify-center text-white flex-shrink-0" style={{ background: color }}><Icon size={17} /></span>
            <span className="min-w-0">
              <span className="block text-[11px] font-semibold text-slate-500 dark:text-slate-400">{label}</span>
              <span className="block text-base font-bold text-slate-800 dark:text-slate-100 truncate">{formatCurrency(value)}</span>
            </span>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-5 items-start">
        {/* Grade do mês */}
        <div className="fin-card p-2.5 sm:p-4">
          <div className="grid grid-cols-7 mb-1.5">
            {WEEKDAYS.map((w) => (
              <div key={w} className="text-center text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-slate-400 py-1">{w}</div>
            ))}
          </div>
          <div className={`grid grid-cols-7 gap-1 sm:gap-1.5 ${loading ? 'opacity-60' : ''}`}>
            {cells.map((day, idx) => {
              if (!day) return <div key={`b-${idx}`} />;
              const dIso = iso(cursor.y, cursor.m, day);
              const list = byDay[dIso] || [];
              const ent = list.filter(i => i.kind === 'entrada').reduce((s, i) => s + i.valor, 0);
              const sai = list.filter(i => i.kind === 'saida').reduce((s, i) => s + i.valor, 0);
              const hasOverdue = list.some(i => i.overdue);
              const hasPending = list.some(i => i.pending && !i.overdue);
              const isToday = dIso === hoje;
              const isSel = dIso === selected;
              return (
                <button
                  key={dIso}
                  type="button"
                  onClick={() => setSelected(dIso)}
                  data-testid={`cal-day-${dIso}`}
                  className={`relative rounded-xl sm:rounded-2xl border text-left p-1 sm:p-1.5 min-h-[52px] sm:min-h-[88px] flex flex-col transition-colors cursor-pointer ${
                    isSel
                      ? 'border-indigo-500 bg-indigo-50/70 dark:bg-indigo-500/10 ring-1 ring-indigo-500'
                      : 'border-slate-200/70 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-white/5'
                  }`}
                >
                  <span className={`text-[11px] sm:text-xs font-bold w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center ${
                    isToday ? 'bg-indigo-500 text-white' : 'text-slate-700 dark:text-slate-200'
                  }`}>{day}</span>

                  {/* Desktop: valores */}
                  <span className="hidden sm:flex flex-col gap-0.5 mt-1 min-w-0">
                    {ent > 0 && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-md bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400 truncate">+{short(ent)}</span>}
                    {sai > 0 && <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md truncate ${hasOverdue ? 'bg-rose-200 text-rose-800 dark:bg-rose-500/25 dark:text-rose-300' : 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-400'}`}>-{short(sai)}</span>}
                  </span>

                  {/* Mobile: pontos */}
                  <span className="sm:hidden flex items-center gap-0.5 mt-auto justify-center pb-0.5">
                    {ent > 0 && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />}
                    {sai > 0 && <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />}
                  </span>

                  {(hasOverdue || hasPending) && (
                    <span className={`absolute top-1 right-1 w-1.5 h-1.5 rounded-full ${hasOverdue ? 'bg-rose-500' : 'bg-amber-400'}`} title={hasOverdue ? 'Em atraso' : 'Pendente'} />
                  )}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 px-1 text-[11px] text-slate-500 dark:text-slate-400">
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" />Entrada</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-rose-500" />Saída</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-400" />Pendente</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-rose-500 ring-2 ring-rose-200" />Em atraso</span>
          </div>
        </div>

        {/* Detalhe do dia */}
        <div className="fin-card p-4 sm:p-5 space-y-3 lg:sticky lg:top-4" data-testid="cal-detalhe">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-sm font-bold text-slate-800 dark:text-slate-100">{selLabel}</p>
              <p className="text-[11px] text-slate-400">{selItems.length} {selItems.length === 1 ? 'item' : 'itens'}</p>
            </div>
            <button
              onClick={() => setModal({ open: true, lancamento: { tipo: 'DESPESA', status: 'pendente', dataCompetencia: selected } })}
              className="nexus-btn-primary inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold"
              data-testid="cal-novo"
            >
              <Plus size={14} /> Novo
            </button>
          </div>

          {selItems.length === 0 ? (
            <p className="text-xs text-slate-400 py-6 text-center">Nada previsto para este dia.</p>
          ) : (
            <ul className="space-y-2">
              {selItems.map((it) => (
                <li key={it.key}>
                  <button
                    type="button"
                    onClick={() => openItem(it)}
                    className="w-full text-left p-3 rounded-xl border border-slate-200/70 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors flex items-center gap-3 cursor-pointer"
                  >
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white ${it.source === 'fatura' ? 'bg-amber-500' : it.kind === 'entrada' ? 'bg-emerald-500' : 'bg-rose-500'}`}>
                      {it.source === 'fatura' ? <CreditCard size={15} /> : it.kind === 'entrada' ? <ArrowUp size={15} /> : <ArrowDown size={15} />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[13px] font-semibold text-slate-800 dark:text-slate-100 truncate">{it.title}</span>
                      <span className="block text-[11px] text-slate-400 truncate">
                        {it.categoria || 'Geral'} ·{' '}
                        <span className={it.overdue ? 'text-rose-500 font-semibold' : it.pending ? 'text-amber-500 font-semibold' : 'text-emerald-500 font-semibold'}>
                          {it.overdue ? 'Em atraso' : it.pending ? (it.kind === 'entrada' ? 'A receber' : 'A pagar') : (it.kind === 'entrada' ? 'Recebido' : 'Pago')}
                        </span>
                      </span>
                    </span>
                    <span className={`text-[13px] font-bold whitespace-nowrap ${it.kind === 'entrada' ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-800 dark:text-slate-100'}`}>
                      {it.kind === 'entrada' ? '+' : '-'}{formatCurrency(it.valor)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <LancamentoModal
        isOpen={modal.open}
        onClose={() => setModal({ open: false, lancamento: null })}
        lancamento={modal.lancamento}
        onSuccess={load}
      />
    </div>
  );
}
